if (!customElements.get('puppy-starter-kit')) {
  customElements.define('puppy-starter-kit', class PuppyStarterKit extends HTMLElement {
    constructor() {
      super();
      this.step = 1;
      this.highestStep = 1;
      this.products = [];
      this.selections = new Map();
      this.categoryMeta = new Map();
      this.loadedCategoryProducts = new Set();
      this.loadingCategoryProducts = new Set();
      this.productPickerCategory = null;
      this.pickerVisibleCounts = new Map();
      this.measurements = { chest: null, neck: null, back: null, bodyLength: null, shoulder: null, snout: null };
      this.unknownMeasures = new Set();
      this.measureLabels = {
        chest: ['Brustumfang', 'Miss an der breitesten Stelle des Brustkorbs, etwa eine Handbreit hinter den Vorderbeinen.'],
        neck: ['Halsumfang', 'Miss dort, wo das Halsband sitzen soll. Zwei Finger sollten bequem dazwischen passen.'],
        back: ['Rückenlänge', 'Miss im geraden Stand vom Halsansatz bis zum Rutenansatz.'],
        bodyLength: ['Körperlänge', 'Miss vom Brustbein bis zum Rutenansatz, während dein Hund gerade steht.'],
        shoulder: ['Schulterhöhe', 'Miss senkrecht vom Boden bis zum höchsten Punkt der Schulter.'],
        snout: ['Fangumfang', 'Miss locker um die breiteste Stelle des geschlossenen Fangs. Dein Welpe muss noch hecheln können.']
      };
    }

    connectedCallback() {
      try { this.products = JSON.parse(this.querySelector('[data-puppy-products]').textContent); } catch (error) { this.products = []; }
      this.currency = this.dataset.currency || 'EUR';
      this.querySelectorAll('[data-category-card]').forEach((card, index) => this.categoryMeta.set(card.dataset.category, {
        label: card.dataset.label,
        url: card.dataset.url,
        order: index
      }));
      this.bindEvents();
      this.buildRecommendations(true);
      this.updateUI();
    }

    bindEvents() {
      this.querySelector('[data-next]').addEventListener('click', () => this.goNext());
      this.querySelector('[data-prev]').addEventListener('click', () => this.goPrev());
      this.querySelector('[data-mobile-prev]').addEventListener('click', () => this.goPrev());
      this.querySelector('[data-mobile-next]').addEventListener('click', () => this.step === 4 ? this.addToCart() : this.goNext());
      this.querySelector('[data-add-to-cart]').addEventListener('click', () => this.addToCart());
      this.querySelectorAll('[data-step-jump]').forEach((button) => button.addEventListener('click', () => {
        const target = Number(button.dataset.stepJump);
        if (!button.disabled && target <= this.highestStep) this.showStep(target);
      }));
      this.addEventListener('change', (event) => {
        const control = event.target;
        if (control.name === 'measure-unknown') {
          ['chest', 'neck'].forEach((key) => {
            const input = this.querySelector(`[name="puppy-${key}"]`);
            input.disabled = control.checked;
            if (control.checked) { input.value = ''; this.measurements[key] = null; this.unknownMeasures.add(key); }
            else this.unknownMeasures.delete(key);
          });
        }
        if (control.matches('[name="puppy-chest"], [name="puppy-neck"]')) {
          const key = control.name.replace('puppy-', '');
          this.measurements[key] = Number(control.value) || null;
          if (control.value) this.unknownMeasures.delete(key);
        }
        if (control.matches('[data-measure-input]')) {
          const key = control.dataset.measureInput;
          this.measurements[key] = Number(control.value) || null;
          if (control.value) this.unknownMeasures.delete(key);
          this.recheckSizeSelections();
          this.renderRecommendations();
        }
        if (control.matches('[data-measure-unknown]')) {
          const key = control.dataset.measureUnknown;
          const input = this.querySelector(`[data-measure-input="${key}"]`);
          if (control.checked) { this.unknownMeasures.add(key); this.measurements[key] = null; if (input) { input.value = ''; input.disabled = true; } }
          else { this.unknownMeasures.delete(key); if (input) input.disabled = false; }
          this.recheckSizeSelections();
          this.renderRecommendations();
        }
        if (control.matches('[name^="category-"]') || control.matches('[name="puppy-size"], [name="puppy-fur"]')) {
          this.buildRecommendations(false);
          if (this.step === 3) this.renderRecommendations();
        }
        this.updateUI();
      });
      this.querySelector('[name="puppy-name"]').addEventListener('input', () => this.updateHeadings());
    }

    state() {
      const selected = (name) => this.querySelector(`[name="${name}"]:checked`)?.value;
      const categoryStates = new Map();
      this.categoryMeta.forEach((meta, key) => categoryStates.set(key, selected(`category-${key}`) || 'skip'));
      return {
        name: this.querySelector('[name="puppy-name"]').value.trim(),
        age: this.querySelector('[name="puppy-age"]').value,
        weight: Number(this.querySelector('[name="puppy-weight"]').value) || null,
        fur: this.querySelector('[name="puppy-fur"]').value,
        size: selected('puppy-size') || 'small',
        categoryStates
      };
    }

    validatePuppy() {
      const invalid = [...this.querySelectorAll('[data-step="1"] input')].find((input) => input.value && !input.checkValidity());
      if (invalid) { invalid.reportValidity(); return false; }
      return true;
    }

    goNext() {
      if (this.step === 1 && !this.validatePuppy()) return;
      if (this.step === 2) this.buildRecommendations(false);
      if (this.step === 3 && !this.validateSelections()) return;
      if (this.step < 4) this.showStep(this.step + 1);
    }

    goPrev() { if (this.step > 1) this.showStep(this.step - 1); }

    showStep(step) {
      this.step = step;
      this.highestStep = Math.max(this.highestStep, step);
      this.querySelectorAll('[data-step]').forEach((panel) => {
        const active = Number(panel.dataset.step) === step;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });
      this.querySelectorAll('[data-step-jump]').forEach((button) => {
        const value = Number(button.dataset.stepJump);
        button.disabled = value > this.highestStep;
        button.classList.toggle('is-active', value === step);
        button.classList.toggle('is-complete', value < step);
        value === step ? button.setAttribute('aria-current', 'step') : button.removeAttribute('aria-current');
      });
      this.querySelector('[data-prev]').hidden = step === 1;
      this.querySelector('[data-next]').hidden = step === 4;
      if (step === 3) this.renderRecommendations();
      if (step === 4) this.renderFinal();
      this.updateUI();
      this.querySelector('.puppy-kit__steps').scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    candidatesFor(category) {
      const state = this.state();
      const all = this.products.filter((item) => item.category === category && item.product.variants.some((variant) => variant.available));
      const ranked = [...all].sort((a, b) => {
        const aScore = (a.audience === state.size ? 4 : a.audience === 'all' ? 2 : 0) + (a.fur === state.fur ? 2 : a.fur === 'all' ? 1 : 0);
        const bScore = (b.audience === state.size ? 4 : b.audience === 'all' ? 2 : 0) + (b.fur === state.fur ? 2 : b.fur === 'all' ? 1 : 0);
        return bScore - aScore;
      });
      return ranked;
    }

    async loadCategoryProducts(category, button) {
      if (this.loadedCategoryProducts.has(category)) {
        this.productPickerCategory = category;
        this.renderRecommendations();
        this.querySelector(`[data-product-picker="${category}"]`)?.focus();
        return;
      }
      if (this.loadingCategoryProducts.has(category)) return;
      const meta = this.categoryMeta.get(category);
      if (!meta?.url) return;
      this.loadingCategoryProducts.add(category);
      if (button) { button.disabled = true; button.textContent = 'Produkte werden geladen …'; }
      try {
        const collectionUrl = new URL(meta.url, window.location.origin);
        const endpoint = `${collectionUrl.pathname.replace(/\/$/, '')}/products.json?limit=250`;
        const response = await fetch(endpoint, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Kategorieprodukte konnten nicht geladen werden.');
        const payload = await response.json();
        const existingProductIds = new Set(this.products.map((item) => String(item.product.id)));
        const additions = (payload.products || []).filter((product) => !existingProductIds.has(String(product.id))).map((product) => {
          const variants = (product.variants || []).map((variant) => ({
            id: Number(variant.id),
            title: variant.title || 'Standard',
            available: Boolean(variant.available),
            price: Math.round((Number.parseFloat(variant.price) || 0) * 100),
            compareAtPrice: Math.round((Number.parseFloat(variant.compare_at_price) || 0) * 100),
            image: typeof variant.featured_image === 'string' ? variant.featured_image : variant.featured_image?.src || null
          }));
          const firstImage = product.images?.[0];
          return {
            blockId: `category-${category}-${product.id}`,
            category,
            audience: 'all',
            fur: 'all',
            rationale: `Alternative aus der PaderPets-Kategorie ${meta.label}. Bitte Eignung und Herstellerangaben prüfen.`,
            quantity: 1,
            requiredMeasures: 'none',
            sizeData: '',
            manualSelection: true,
            product: {
              id: Number(product.id),
              title: product.title,
              handle: product.handle,
              url: `/products/${product.handle}`,
              image: typeof firstImage === 'string' ? firstImage : firstImage?.src || null,
              description: product.body_html || '',
              variants
            }
          };
        }).filter((item) => item.product.variants.some((variant) => variant.available));
        this.products.push(...additions);
        this.loadedCategoryProducts.add(category);
        this.productPickerCategory = category;
        this.renderRecommendations();
        this.updateUI();
        this.querySelector(`[data-product-picker="${category}"]`)?.focus();
      } catch (error) {
        if (button) { button.disabled = false; button.textContent = 'Erneut versuchen'; }
      } finally {
        this.loadingCategoryProducts.delete(category);
      }
    }

    chooseProduct(category, blockId) {
      const choice = this.selections.get(category);
      const selectedItem = this.products.find((candidate) => candidate.blockId === blockId);
      if (!choice || !selectedItem) return;
      choice.blockId = selectedItem.blockId;
      choice.included = true;
      choice.quantity = selectedItem.quantity || 1;
      const result = this.chooseVariant(selectedItem, null);
      choice.variantId = result.variant?.id || null;
      choice.status = selectedItem.manualSelection ? 'manual' : result.status;
      choice.statusText = selectedItem.manualSelection ? 'Produkt manuell gewählt – bitte Eignung, Maße und Herstellerangaben prüfen.' : result.text;
      this.productPickerCategory = null;
      this.renderRecommendations();
      this.updateUI();
    }

    buildRecommendations(reset) {
      const state = this.state();
      this.categoryMeta.forEach((meta, category) => {
        const needed = state.categoryStates.get(category) === 'need';
        const candidates = this.candidatesFor(category);
        const old = this.selections.get(category);
        const item = (!reset && old && candidates.find((entry) => entry.blockId === old.blockId)) || candidates[0];
        if (!item) {
          this.selections.set(category, { blockId: null, variantId: null, quantity: 1, included: false, status: 'unavailable' });
          return;
        }
        const sizeResult = this.chooseVariant(item, !reset ? old?.variantId : null);
        this.selections.set(category, {
          blockId: item.blockId,
          variantId: sizeResult.variant?.id || null,
          quantity: old?.quantity || item.quantity || 1,
          included: needed && (reset ? true : old?.included !== false),
          status: sizeResult.status,
          statusText: sizeResult.text
        });
      });
    }

    recheckSizeSelections() {
      this.selections.forEach((choice, category) => {
        const item = this.itemFor(category);
        if (!item || !choice.included) return;
        const result = this.chooseVariant(item, null);
        choice.variantId = result.variant?.id || null;
        choice.status = result.status;
        choice.statusText = result.text;
      });
    }

    requiredKeys(item) {
      return {
        none: [], chest: ['chest'], neck: ['neck'], chest_neck: ['chest', 'neck'], back: ['back'], back_chest_neck: ['back', 'chest', 'neck'], body: ['bodyLength', 'shoulder'], snout: ['snout']
      }[item?.requiredMeasures] || [];
    }

    parseRules(item) {
      const aliases = { chest: ['brustumfang', 'bauchumfang', 'chest'], neck: ['halsumfang', 'neck'], back: ['rückenlänge', 'rueckenlaenge', 'back'], bodyLength: ['körperlänge', 'koerperlaenge', 'bodylength'], shoulder: ['schulterhöhe', 'schulterhoehe', 'shoulder'], snout: ['fangumfang', 'maulumfang', 'umfang schnauze', 'snout'] };
      const rules = [];
      if (item.sizeData?.trim()) {
        item.sizeData.split(/\r?\n/).filter(Boolean).forEach((line) => {
          const parts = line.split('|').map((part) => part.trim());
          const measures = {};
          parts.slice(1).forEach((part) => {
            const match = part.match(/^([a-zA-Z]+)\s*:\s*(\d+(?:[.,]\d+)?)\s*[-–]\s*(\d+(?:[.,]\d+)?)/);
            if (match) measures[match[1]] = [Number(match[2].replace(',', '.')), Number(match[3].replace(',', '.'))];
          });
          rules.push({ variantLabel: parts[0].toLowerCase(), measures });
        });
        return rules;
      }
      item.product.variants.forEach((variant) => {
        const measures = {};
        Object.entries(aliases).forEach(([key, names]) => {
          for (const name of names) {
            const range = variant.title.match(new RegExp(`${name}\\s*:?\\s*(\\d+(?:[.,]\\d+)?)\\s*[-–]\\s*(\\d+(?:[.,]\\d+)?)\\s*cm`, 'i'));
            const exact = variant.title.match(new RegExp(`${name}\\s*:?\\s*(\\d+(?:[.,]\\d+)?)\\s*cm`, 'i'));
            if (range) { measures[key] = [Number(range[1].replace(',', '.')), Number(range[2].replace(',', '.'))]; break; }
            if (exact) { const value = Number(exact[1].replace(',', '.')); measures[key] = [value, value]; break; }
          }
        });
        if (Object.keys(measures).length) rules.push({ variantId: variant.id, measures });
      });
      return rules;
    }

    chooseVariant(item, previousId) {
      const available = item.product.variants.filter((variant) => variant.available);
      if (!available.length) return { variant: null, status: 'unavailable', text: 'Derzeit ist keine Variante verfügbar.' };
      if (previousId && available.some((variant) => variant.id === Number(previousId))) return { variant: available.find((variant) => variant.id === Number(previousId)), status: 'manual', text: 'Variante manuell gewählt.' };
      const required = this.requiredKeys(item);
      if (!required.length) return { variant: available[0], status: 'ready', text: 'Verfügbare Variante vorausgewählt.' };
      const missing = required.filter((key) => !this.measurements[key]);
      if (missing.length) return { variant: null, status: 'missing', text: `Für die Größenprüfung fehlt: ${missing.map((key) => this.measureLabels[key][0]).join(', ')}.` };
      const rules = this.parseRules(item);
      if (!rules.length) return { variant: null, status: 'manual', text: 'Die Größentabelle ist nicht eindeutig strukturiert. Bitte wähle die Variante manuell.' };
      const matches = rules.filter((rule) => required.every((key) => rule.measures[key] && this.measurements[key] >= rule.measures[key][0] && this.measurements[key] <= rule.measures[key][1]));
      const uniqueRanges = [...new Map(matches.map((rule) => [JSON.stringify(rule.measures), rule])).values()];
      if (uniqueRanges.length !== 1) return { variant: null, status: uniqueRanges.length ? 'ambiguous' : 'no_match', text: uniqueRanges.length ? 'Mehrere Größen liegen im Grenzbereich. Bitte wähle manuell.' : 'Keine Größe passt eindeutig zu allen angegebenen Maßen. Bitte wähle eine Alternative.' };
      const rule = uniqueRanges[0];
      const variant = rule.variantId ? available.find((entry) => entry.id === rule.variantId) : available.find((entry) => entry.title.toLowerCase().includes(rule.variantLabel));
      return variant ? { variant, status: 'matched', text: `Diese Variante passt zu ${required.map((key) => `${this.measurements[key]} cm ${this.measureLabels[key][0]}`).join(' und ')}.` } : { variant: null, status: 'manual', text: 'Die passende Größenregel konnte keiner verfügbaren Variante zugeordnet werden.' };
    }

    itemFor(category) {
      const choice = this.selections.get(category);
      return this.products.find((item) => item.blockId === choice?.blockId);
    }

    selectedVariant(item, choice) { return item?.product.variants.find((variant) => variant.id === Number(choice?.variantId)) || null; }

    selectedCategories() {
      const state = this.state();
      return [...this.categoryMeta.keys()].filter((key) => state.categoryStates.get(key) === 'need').sort((a, b) => this.categoryMeta.get(a).order - this.categoryMeta.get(b).order);
    }

    renderMeasureRequests() {
      const host = this.querySelector('[data-measure-requests]');
      const required = new Set();
      this.selectedCategories().forEach((category) => {
        const item = this.itemFor(category);
        this.requiredKeys(item).forEach((key) => required.add(key));
      });
      const dynamic = [...required].filter((key) => key !== 'chest' && key !== 'neck');
      host.innerHTML = dynamic.map((key) => {
        const [label, help] = this.measureLabels[key];
        return `<div class="puppy-kit__measure-request"><div><p>Für deine Auswahl benötigt</p><h3>${label}</h3><span>${help}</span></div><label class="puppy-kit__field"><span>${label}</span><span class="puppy-kit__input-suffix"><input type="number" min="5" max="180" step="1" value="${this.measurements[key] || ''}" data-measure-input="${key}" ${this.unknownMeasures.has(key) ? 'disabled' : ''}><em>cm</em></span></label><label class="puppy-kit__unknown"><input type="checkbox" data-measure-unknown="${key}" ${this.unknownMeasures.has(key) ? 'checked' : ''}><span>Maß noch unbekannt</span></label></div>`;
      }).join('');
      host.hidden = !dynamic.length;
    }

    renderRecommendations() {
      this.renderMeasureRequests();
      const container = this.querySelector('[data-products]');
      container.innerHTML = '';
      const categories = this.selectedCategories();
      this.querySelector('[data-empty]').hidden = categories.length > 0;
      categories.forEach((category) => {
        const choice = this.selections.get(category);
        const item = this.itemFor(category);
        const meta = this.categoryMeta.get(category);
        const section = document.createElement('section');
        section.className = 'puppy-kit__product-group';
        if (this.productPickerCategory === category) {
          const candidates = this.candidatesFor(category);
          const visibleCount = this.pickerVisibleCounts.get(category) || 12;
          const visibleCandidates = candidates.slice(0, visibleCount);
          section.innerHTML = `<div class="puppy-kit__group-title"><div><p>PaderPets-Kategorie</p><h3>${this.escape(meta.label)}</h3></div></div>
            <div class="puppy-kit__product-picker" data-product-picker="${this.escapeAttr(category)}" tabindex="-1">
              <div class="puppy-kit__picker-header"><div><p>Produkt austauschen</p><h4>Wähle eine Alternative aus</h4><span>Tippe auf ein Bild. Anschließend kannst du die passende Variante festlegen.</span></div><button type="button" data-picker-back>← Zurück zur Empfehlung</button></div>
              ${visibleCandidates.length ? `<div class="puppy-kit__picker-grid">${visibleCandidates.map((candidate) => {
                const current = candidate.blockId === choice?.blockId;
                return `<button type="button" class="puppy-kit__picker-card${current ? ' is-selected' : ''}" data-picker-product="${this.escapeAttr(candidate.blockId)}" aria-pressed="${current}">
                  <span class="puppy-kit__picker-image">${candidate.product.image ? `<img src="${this.escapeAttr(candidate.product.image)}" alt="" loading="lazy" width="360" height="360">` : '<em>Kein Bild</em>'}<small>${candidate.manualSelection ? 'Manuell prüfen' : 'Welpengeprüft'}</small></span>
                  <span class="puppy-kit__picker-copy"><strong>${this.escape(candidate.product.title)}</strong><em>ab ${this.money(this.minimumPrice(candidate))}</em>${current ? '<b>Aktuelle Auswahl</b>' : '<b>Auswählen</b>'}</span>
                </button>`;
              }).join('')}</div>` : '<div class="puppy-kit__product-missing">Momentan sind keine weiteren verfügbaren Produkte in dieser Kategorie vorhanden.</div>'}${visibleCount < candidates.length ? `<button type="button" class="puppy-kit__picker-more" data-picker-more>Mehr Produkte anzeigen (${candidates.length - visibleCount})</button>` : ''}
            </div>`;
          section.querySelector('[data-picker-back]')?.addEventListener('click', () => { this.productPickerCategory = null; this.renderRecommendations(); });
          section.querySelector('[data-picker-more]')?.addEventListener('click', () => { this.pickerVisibleCounts.set(category, visibleCount + 12); this.renderRecommendations(); this.querySelectorAll(`[data-product-picker="${category}"] [data-picker-product]`)[visibleCount]?.focus(); });
          section.querySelectorAll('[data-picker-product]').forEach((button) => button.addEventListener('click', () => this.chooseProduct(category, button.dataset.pickerProduct)));
          container.appendChild(section); return;
        }
        if (!item) {
          section.innerHTML = `<div class="puppy-kit__group-title"><div><p>PaderPets-Kategorie</p><h3>${this.escape(meta.label)}</h3></div>${meta.url ? `<a href="${this.escapeAttr(meta.url)}">Kategorie ansehen →</a>` : ''}</div><div class="puppy-kit__product-missing">Für diese Kategorie ist aktuell kein welpengeeigneter, verfügbarer Vorschlag freigegeben.${meta.url ? `<br><button type="button" data-load-products>${this.loadedCategoryProducts.has(category) ? 'Andere Produkte ansehen' : 'Verfügbare Produkte der Kategorie laden'}</button>` : ''}</div>`;
          section.querySelector('[data-load-products]')?.addEventListener('click', (event) => this.loadCategoryProducts(category, event.currentTarget));
          container.appendChild(section); return;
        }
        const variant = this.selectedVariant(item, choice);
        const candidates = this.candidatesFor(category);
        const modelCandidates = candidates.filter((candidate) => !candidate.manualSelection || candidate.blockId === item.blockId);
        const statusClass = ['matched', 'ready'].includes(choice.status) ? 'is-good' : choice.status === 'no_match' ? 'is-alert' : '';
        section.innerHTML = `<div class="puppy-kit__group-title"><div><p>PaderPets-Kategorie</p><h3>${this.escape(meta.label)}</h3></div>${meta.url ? `<a href="${this.escapeAttr(meta.url)}">Kategorie ansehen →</a>` : ''}</div>
          <article class="puppy-kit__product${choice.included ? '' : ' is-removed'}" data-product-card>
            <div class="puppy-kit__product-media">${item.product.image ? `<img src="${this.escapeAttr(variant?.image || item.product.image)}" alt="" loading="lazy" width="300" height="300">` : ''}<span>${item.manualSelection ? 'Manuelle Auswahl' : 'Welpengeprüft'}</span></div>
            <div class="puppy-kit__product-content"><div class="puppy-kit__product-top"><div><p class="puppy-kit__product-kicker">Unsere Empfehlung</p><h4>${this.escape(item.product.title)}</h4></div><strong class="puppy-kit__product-price">${this.money((variant?.price || this.minimumPrice(item)) * choice.quantity)}</strong></div>
              <p class="puppy-kit__product-copy">${this.escape(item.rationale || '')}</p><p class="puppy-kit__fit ${statusClass}">${this.escape(choice.statusText || '')}</p>
              <div class="puppy-kit__product-controls"><label><span>Vorgeschlagenes Produkt ändern</span><select data-model>${modelCandidates.map((candidate) => `<option value="${candidate.blockId}" ${candidate.blockId === item.blockId ? 'selected' : ''}>${this.escape(candidate.product.title)} · ab ${this.money(this.minimumPrice(candidate))}${candidate.manualSelection ? ' · manuell prüfen' : ''}</option>`).join('')}</select></label>
                <label><span>Größe / Farbe / Variante</span><select data-variant><option value="">Bitte wählen</option>${item.product.variants.map((option) => `<option value="${option.id}" ${option.id === variant?.id ? 'selected' : ''} ${option.available ? '' : 'disabled'}>${this.escape(option.title)} · ${this.money(option.price)}${option.available ? '' : ' – ausverkauft'}</option>`).join('')}</select></label></div>
              <div class="puppy-kit__product-links"><a href="${this.escapeAttr(item.product.url)}">Produktdetails</a><button type="button" data-toggle>${choice.included ? 'Aus Set entfernen' : 'Wieder hinzufügen'}</button>${meta.url ? `<button type="button" data-load-products>${this.loadedCategoryProducts.has(category) ? 'Andere Produkte ansehen' : 'Weitere Produkte laden'}</button>` : ''}</div>
            </div>
          </article>`;
        section.querySelector('[data-model]')?.addEventListener('change', (event) => {
          this.chooseProduct(category, event.target.value);
        });
        section.querySelector('[data-load-products]')?.addEventListener('click', (event) => this.loadCategoryProducts(category, event.currentTarget));
        section.querySelector('[data-variant]').addEventListener('change', (event) => {
          choice.variantId = event.target.value ? Number(event.target.value) : null;
          if (choice.variantId) { choice.status = 'manual'; choice.statusText = 'Variante manuell gewählt – bitte Herstellerangaben prüfen.'; }
          this.renderRecommendations(); this.updateUI();
        });
        section.querySelector('[data-toggle]').addEventListener('click', () => { choice.included = !choice.included; this.renderRecommendations(); this.updateUI(); });
        container.appendChild(section);
      });
    }

    validateSelections() {
      const invalidCategory = this.selectedCategories().find((category) => {
        const choice = this.selections.get(category);
        return choice?.included && (!choice.blockId || !choice.variantId);
      });
      if (!invalidCategory) return true;
      const group = [...this.querySelectorAll('.puppy-kit__product-group')].find((node) => node.querySelector('h3')?.textContent === this.categoryMeta.get(invalidCategory).label);
      const select = group?.querySelector('[data-variant]');
      if (select) { select.setCustomValidity('Bitte wähle eine verfügbare Variante.'); select.reportValidity(); select.addEventListener('change', () => select.setCustomValidity(''), { once: true }); }
      else group?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    activeLines() {
      const lines = [];
      const seenProducts = new Set();
      this.selectedCategories().forEach((category) => {
        const choice = this.selections.get(category); const item = this.itemFor(category); const variant = this.selectedVariant(item, choice);
        if (!choice?.included || !item || !variant?.available || seenProducts.has(item.product.id)) return;
        seenProducts.add(item.product.id); lines.push({ category, item, variant, quantity: choice.quantity || 1 });
      });
      return lines;
    }

    renderFinal() {
      const lines = this.activeLines();
      this.querySelector('[data-final-list]').innerHTML = lines.length ? lines.map(({ category, item, variant, quantity }) => `<div class="puppy-kit__final-item" data-final-category="${category}">
        ${item.product.image ? `<img src="${this.escapeAttr(variant.image || item.product.image)}" alt="" loading="lazy" width="120" height="120">` : '<span></span>'}<div><small>${this.escape(this.categoryMeta.get(category).label)}</small><b>${this.escape(item.product.title)}</b><span>${this.escape(variant.title)}</span><em>${this.escape(this.dataset.deliveryNote)}</em></div>
        <label class="puppy-kit__quantity"><span>Menge</span><input type="number" min="1" max="5" value="${quantity}" data-final-quantity="${category}"></label><strong>${this.money(variant.price * quantity)}</strong></div>`).join('') : '<div class="puppy-kit__product-missing">Dein Set enthält noch keine vollständig ausgewählten Artikel.</div>';
      this.querySelectorAll('[data-final-quantity]').forEach((input) => input.addEventListener('change', () => {
        const choice = this.selections.get(input.dataset.finalQuantity); choice.quantity = Math.max(1, Math.min(5, Number(input.value) || 1)); this.renderFinal(); this.updateUI();
      }));
      this.querySelector('[data-add-to-cart]').disabled = lines.length === 0;
    }

    minimumPrice(item) { return Math.min(...item.product.variants.filter((variant) => variant.available).map((variant) => variant.price), Infinity); }

    total() { return this.activeLines().reduce((sum, line) => sum + line.variant.price * line.quantity, 0); }

    updateHeadings() {
      const name = this.state().name;
      this.querySelector('#puppy-kit-step-3').textContent = name ? `Dein Starterkit für ${name}` : 'Dein passendes Starterkit';
    }

    updateUI() {
      this.updateHeadings();
      const lines = this.activeLines(); const count = lines.reduce((sum, line) => sum + line.quantity, 0); const total = this.total(); const countText = `${count} Artikel`;
      const summaryCount = this.querySelector('[data-summary-count]'); const summaryList = this.querySelector('[data-summary-list]'); const totalRow = this.querySelector('.puppy-kit__total');
      const mobileCount = this.querySelector('[data-mobile-count]'); const mobileTotal = this.querySelector('[data-mobile-total]'); const revealSet = this.step >= 3;
      const mobileStepName = ['Dein Welpe', 'Dein Bedarf', 'Dein Set', 'Überblick'][this.step - 1];
      summaryCount.hidden = !revealSet; totalRow.hidden = !revealSet; mobileTotal.hidden = !revealSet;
      if (revealSet) {
        summaryCount.textContent = countText; mobileCount.textContent = `${this.step}/4 · ${mobileStepName} · ${countText}`; this.querySelector('[data-total]').textContent = this.money(total); mobileTotal.textContent = this.money(total);
        summaryList.innerHTML = lines.length ? lines.map(({ category, item, variant, quantity }) => `<div class="puppy-kit__summary-item"><small>${this.escape(this.categoryMeta.get(category).label)}</small><b>${this.escape(item.product.title)}${quantity > 1 ? ` × ${quantity}` : ''}</b><span>${this.money(variant.price * quantity)}</span><em>${this.escape(variant.title)}</em></div>`).join('') : '<p class="puppy-kit__summary-empty">Deine Auswahl erscheint hier.</p>';
      } else {
        mobileCount.textContent = `${this.step}/4 · ${mobileStepName}`;
        summaryList.innerHTML = '<p class="puppy-kit__summary-empty">Beantworte zuerst die kurzen Fragen. Deine Produktauswahl und der Gesamtpreis erscheinen anschließend.</p>';
      }
      const mobilePrev = this.querySelector('[data-mobile-prev]'); const mobileNext = this.querySelector('[data-mobile-next]');
      mobilePrev.hidden = this.step === 1;
      mobileNext.classList.toggle('is-full', this.step === 1);
      mobileNext.textContent = this.step === 4 ? 'In den Warenkorb' : 'Weiter →';
    }

    async addToCart() {
      const button = this.querySelector('[data-add-to-cart]'); const errorBox = this.querySelector('[data-cart-error]'); const lines = this.activeLines();
      if (!lines.length || button.getAttribute('aria-busy') === 'true') return;
      button.setAttribute('aria-busy', 'true'); errorBox.hidden = true;
      try {
        const response = await fetch(this.dataset.cartAddUrl, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify({ items: lines.map(({ category, variant, quantity }) => ({ id: variant.id, quantity, properties: { Starterkit: this.state().name || 'Welpe', Kategorie: this.categoryMeta.get(category).label } })) }) });
        const result = await response.json(); if (!response.ok) throw new Error(result.description || result.message || 'Das Set konnte nicht hinzugefügt werden.'); window.location.href = this.dataset.cartUrl;
      } catch (error) { errorBox.textContent = error.message || 'Das Set konnte nicht hinzugefügt werden. Bitte versuche es erneut.'; errorBox.hidden = false; }
      finally { button.removeAttribute('aria-busy'); }
    }

    money(cents) { return new Intl.NumberFormat('de-DE', { style: 'currency', currency: this.currency }).format((Number(cents) || 0) / 100); }
    escape(value) { const node = document.createElement('span'); node.textContent = value ?? ''; return node.innerHTML; }
    escapeAttr(value) { return this.escape(value).replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/`/g, '&#96;'); }
  });
}
